/**
 * The bridge to the ERP Artwork Manager desktop app.
 *
 * The desktop app is an Electron shell that loads this website. Its preload
 * script exposes exactly one object, `window.artManager`, with:
 *
 *   isElectron   true
 *   version      the app's own version string
 *   openFolder   (absolutePath) => Promise<{ ok, message }>
 *
 * Nothing else in the web app should touch `window.artManager` directly --
 * go through these helpers, so a page never has to know whether it is in a
 * browser or in the app, and the bridge's shape is defined in one place on
 * this side (the other place is art-manager/preload.js).
 */

const bridge = () =>
  typeof window !== "undefined" && window.artManager?.isElectron
    ? window.artManager
    : null;

/** True only when the page is running inside the desktop app. */
export const isArtManager = () => !!bridge();

/** The desktop app's version, or null in a browser. */
export const artManagerVersion = () => bridge()?.version || null;

/**
 * Ask the desktop app to open a folder in Explorer. Resolves to
 * { ok, message }; never throws. In a browser it resolves ok:false with a
 * message that says why, so callers can show one result path either way.
 */
export const openFolder = async (path) => {
  const b = bridge();
  if (!b) {
    return {
      ok: false,
      message: "Open this page in the ERP Artwork Manager app to use it.",
    };
  }
  try {
    const result = await b.openFolder(path);
    return result && typeof result === "object"
      ? { ok: !!result.ok, message: result.message || "" }
      : { ok: false, message: "The app gave no answer." };
  } catch (err) {
    return { ok: false, message: err?.message || "Could not open the folder." };
  }
};
